import {
  AdresseTaaS,
  Auslandsrisiko,
  FahrradWert,
  HausArt,
  SpartenType,
  Stockwerk,
  TaasSalesProductName,
  TaasVhvV2Data,
  VorschadenArt,
  Wohnungslage
} from '@ps20/bff-models';
import {
  convertProduktNameToTaasProduktName,
  convertVhVV2TaasBausteinToTarifrisikoschluessel,
  DIPLOMATEN_CODE,
  Produktlinie
} from '@ps20-frontend/frontend-models';
import {
  buildBasicSalesProductsForShop,
  buildBasicSalesProductsForStrecke,
  getSelectedProduktlinie,
  parseDatum,
} from '@ps20-frontend/preis-lib';
import { Wertsachensumme } from '../../model/wertsachensummen.model';
import { IAppState } from '../../appState';
import { VersicherungssummeForLinie } from '../../action-types/versicherungssummenResponseReceived';
import { FahradwertForLinie } from '../../model/fahradwertForLinie.model';
import {
  EntschaedigungsgrenzeRadForLinie,
  isEntschaedigungsgrenzeResetFromBff,
} from '../../model/entschaedigungsgrenzeRad.model';
import {
  ERDBEBEN_SELBSTBETEILIGUNG,
  extremwetterschutzSelbstbeteiligungToBetrag,
} from '../../model/extremwetterschutzSelbstbeteiligungValues.model';
import {
  getEntschaedigungsgrenzeInEuro,
  isTragerisikoComplete,
  TragerisikoWerte,
} from '../../model/tragerisiko.model';
import { setGisDataToVhvData } from './gisDaten.transformer';

function applyTragerisikoWerte(vhvData: TaasVhvV2Data, tragerisiko: TragerisikoWerte): void {
  if (!isTragerisikoComplete(tragerisiko)) {
    return;
  }
  vhvData.versicherungssummeTrageversicherung = getEntschaedigungsgrenzeInEuro(tragerisiko);
  vhvData.tragHoechsterEinzelwert = tragerisiko.einzelwert;
}

function applyEntschaedigungsgrenzeRad(
  vhvData: TaasVhvV2Data,
  produktlinien: Array<Produktlinie>,
  entschaedigungsgrenzen: Array<EntschaedigungsgrenzeRadForLinie>,
): void {
  const map: { [index: string]: number } = {};
  produktlinien.forEach(produktlinie => {
    const linie = entschaedigungsgrenzen?.find(
      grenze => grenze.produktlinie === produktlinie.produktName,
    );
    if (
      linie?.available &&
      linie.betrag != null &&
      !isEntschaedigungsgrenzeResetFromBff(linie)
    ) {
      map[convertProduktNameToTaasProduktName(produktlinie.produktName)] = linie.betrag;
    }
  });

  if (Object.keys(map).length > 0) {
    vhvData.entschaedigungsgrenzeRad = map;
  }
}

export function buildTaasVHVDataForStrecke(state: IAppState): TaasVhvV2Data {
  let vhvData: TaasVhvV2Data = generateBasicTaasVHVData(state);

  const produkte: Array<Produktlinie> = state.produkt.produktlinien;
  vhvData.versicherungssumme = getVersicherungssummeMap(produkte, state.versicherungssummen);
  vhvData.entschaedigungsgrenzeWertsachenBetrag = addWertsachenSummenToVHVData(
    vhvData,
    produkte,
    state.wertsachensummen,
  );
  vhvData.versicherungssummenRad = getFahrradsummeMap(produkte, state.versicherungssummeFahrrad);
  applyEntschaedigungsgrenzeRad(vhvData, produkte, state.entschaedigungsgrenzeRad);
  applyTragerisikoWerte(vhvData, state.tragerisiko);
  vhvData = setGisDataToVhvData(vhvData, state.gisDaten);
  if (state.plzFaktoren) {
    vhvData.plzFaktorenAusgabe = { ...state.plzFaktoren };
  }

  vhvData.salesProducts = buildBasicSalesProductsForStrecke(
    state.produkt,
    convertVhVV2TaasBausteinToTarifrisikoschluessel,
  );
  return vhvData;
}

export function buildTaasVHVDataForShop(state: IAppState): TaasVhvV2Data {
  let vhvData: TaasVhvV2Data = generateBasicTaasVHVData(state);

  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);
  if (!state.vertragsoptionen?.beibehalten) {
    vhvData.versicherungssumme = getVersicherungssummeMap(
      [selectedProduktlinie],
      state.versicherungssummen,
    );
    vhvData.entschaedigungsgrenzeWertsachenBetrag = addWertsachenSummenToVHVData(
      vhvData,
      [selectedProduktlinie],
      state.wertsachensummen,
    );
    vhvData.versicherungssummenRad = getFahrradsummeMap(
      [selectedProduktlinie],
      state.versicherungssummeFahrrad,
    );
  } else {
    const salesProduktName: TaasSalesProductName = convertProduktNameToTaasProduktName(
      selectedProduktlinie.produktName,
    );
    vhvData.versicherungssumme = { [salesProduktName]: state.vertragsoptionen.versicherungssumme };
    vhvData.entschaedigungsgrenzeWertsachenBetrag = {
      [salesProduktName]: state.wertsachensummen?.find(
        value => value.produktline === selectedProduktlinie.produktName,
      )?.summe,
    };
    vhvData.versicherungssummenRad = {
      [salesProduktName]: state.versicherungssummeFahrrad?.find(
        value => value.produktlinie === selectedProduktlinie.produktName,
      )?.summe,
    };
    if (state.versObjekt.plzFaktorErdb) {
      vhvData.plzFaktorErdb = state.versObjekt.plzFaktorErdb;
    }
    vhvData.plzFaktorGla = state.versObjekt.plzFaktorGla;
    vhvData.plzFaktorHws = state.versObjekt.plzFaktorHws;
    vhvData.ueberSchwemmungSelbstbehalt = state.versObjekt.selbstbehaltUeberschwemmung;
  }
  applyEntschaedigungsgrenzeRad(vhvData, [selectedProduktlinie], state.entschaedigungsgrenzeRad);
  applyTragerisikoWerte(vhvData, state.tragerisiko);
  vhvData = setGisDataToVhvData(vhvData, state.gisDaten);
  if (state.plzFaktoren) {
    vhvData.plzFaktorenAusgabe = { ...state.plzFaktoren };
  }

  vhvData.salesProducts = buildBasicSalesProductsForShop(
    state.produkt,
    convertVhVV2TaasBausteinToTarifrisikoschluessel,
  );

  return vhvData;
}

function convertRisikoortToAdresseTaaS(state: IAppState): AdresseTaaS {
  let auslandsrisiko;

  if (state.vertragsoptionen?.selectedKooperation === DIPLOMATEN_CODE) {
    auslandsrisiko = state.risikoort?.auslandsrisiko || Auslandsrisiko.RISIKO_DEUTSCHLAND;
  } else {
    auslandsrisiko = undefined;
  }

  return {
    plz: state.risikoort?.plz,
    ort: state.risikoort?.ort,
    strasse: state.risikoort?.strasse,
    hausnummer: state.risikoort?.hausnummer,
    latitude: state.risikoort?.latitude,
    longtitude: state.risikoort?.longtitude,
    auslandsrisiko: auslandsrisiko,
    auslaendischeAdresse: state.risikoort?.isImmobilieImAusland,
  };
}

export function generateBasicTaasVHVData(state: IAppState): TaasVhvV2Data {
  return {
    spartenType: SpartenType.HAUSRAT_V2,
    adresse: convertRisikoortToAdresseTaaS(state),
    wohnflaecheWohnung: state.versObjekt.wohnflaeche,
    vorschaden: [
      {
        vorschadenArt: VorschadenArt.FELS,
        anzahl: state.vorschaeden.fels,
        manuallyEntered: state.vorschaeden.felsTouched,
      },
      {
        vorschadenArt: VorschadenArt.RAD,
        anzahl: state.vorschaeden.fahrrad,
        manuallyEntered: state.vorschaeden.fahrradTouched,
      },
      {
        vorschadenArt: VorschadenArt.ELEMENTAR,
        anzahl: state.vorschaeden.elementar,
        manuallyEntered: state.vorschaeden.elementarTouched,
      },
    ],
    geburtsdatumVN: parseDatum(state.versicherungsnehmer.geburtsdatum),
    familienStand: state.versicherungsnehmer.familienstand,
    wohnungslage: convertWohnungslage(state.versObjekt.hausart, state.versObjekt.stockwerk),
    wohnungBewohnt: state.versObjekt.staendigBewohnt,
    innerorts: state.versObjekt.gebaeudeInnerhalbWohngebiet,
    gebauedeBewohnt: state.versObjekt.gebaeudeStaendigBewohnt,
    versicherungssummenRad: null,
    versicherungssumme: null,
    entschaedigungsgrenzeWertsachenBetrag: null,
    salesProducts: null,
    urbeginndatum: state.vertragsoptionen?.urbeginn,
    internerVertragsbezugs: state.taasInfo?.internerVertragsbezugs,
    erdbebenSelbstbehalt: ERDBEBEN_SELBSTBETEILIGUNG,
    ueberSchwemmungSelbstbehalt: extremwetterschutzSelbstbeteiligungToBetrag(
      state.extremwetterschutzSelbstbeteiligung,
    ),
    kunstHoechsterEinzelwert: state.hoechsteEinzelwerte?.kunstHoechsterEinzelwert ?? 0,
    schmuckHoechsterEinzelwert: state.hoechsteEinzelwerte?.schmuckHoechsterEinzelwert ?? 0,
    uhrHoechsterEinzelwert: state.hoechsteEinzelwerte?.uhrHoechsterEinzelwert ?? 0,
  };
}

export function convertWohnungslage(hausart: HausArt, stockwerk: Stockwerk): Wohnungslage {
  if (hausart === HausArt.EINFAMILIENHAUS) {
    return Wohnungslage.EINFAMILIENHAUS;
  } else if (hausart === HausArt.MEHRFAMILIENHAUS) {
    if (stockwerk === Stockwerk.ERDGESCHOSS) {
      return Wohnungslage.MEHRFAMILIENHAUS;
    } else if (stockwerk === Stockwerk.OBERGESCHOSS) {
      return Wohnungslage.MEHRFAMILIENHAUS_OBERGESCHOSS;
    }
  }
  return null;
}

export function getFahrradsummeMap(
  produktlinien: Array<Produktlinie>,
  fahrradsummen: Array<FahradwertForLinie>,
): { [index: string]: FahrradWert } {
  const fahrradsummenMap: { [index: string]: FahrradWert } = {};

  produktlinien.forEach(produklinie => {
    fahrradsummenMap[convertProduktNameToTaasProduktName(produklinie.produktName)] =
      getFahrradsumme(fahrradsummen, produklinie);
  });

  return fahrradsummenMap;
}

function getFahrradsumme(
  versicherungssummen: Array<FahradwertForLinie>,
  produklinie: Produktlinie,
): FahrradWert {
  const fahrradsummeForLinie = versicherungssummen.find(
    summe => summe.produktlinie === produklinie.produktName,
  );

  if (fahrradsummeForLinie) {
    return fahrradsummeForLinie.summe;
  }

  throw new Error(
    `vhvDataTransformer.getFahrradsumme: Keine Fahrradsumme für Linie ${produklinie.produktName} gefunden!`,
  );
}

export function getVersicherungssummeMap(
  produktlinien: Array<Produktlinie>,
  versicherungssummen: Array<VersicherungssummeForLinie>,
): { [index: string]: number } {
  const versicherungssummeMap: { [index: string]: number } = {};

  produktlinien.forEach(produklinie => {
    versicherungssummeMap[convertProduktNameToTaasProduktName(produklinie.produktName)] =
      getVersicherungssumme(versicherungssummen, produklinie);
  });

  return versicherungssummeMap;
}

function getVersicherungssumme(
  versicherungssummen: Array<VersicherungssummeForLinie>,
  produktlinie: Produktlinie,
): number {
  const versicherungssummenForLinie = versicherungssummen.find(
    summe => summe.produktlinie === produktlinie.produktName,
  );

  if (versicherungssummenForLinie) {
    return versicherungssummenForLinie.summe;
  }

  throw new Error(
    `vhvDataTransformer.getVersicherungssume: Keine Versicherungssumme für Linie ${produktlinie.produktName} gefunden!. vs: ${JSON.stringify(versicherungssummen)}, pl: ${JSON.stringify(produktlinie)}`,
  );
}

export function addWertsachenSummenToVHVData(
  vhvData: TaasVhvV2Data,
  produktlinien: Array<Produktlinie>,
  wertsachensummen: Array<Wertsachensumme>,
): { [p: string]: number } {
  const wertsachensummeMap: { [index: string]: number } = {};

  produktlinien.forEach(produktlinie => {
    const wertsachensumme: Wertsachensumme = getWertsachensummeForProduktlinie(
      wertsachensummen,
      produktlinie,
    );

    if (wertsachensumme) {
      wertsachensummeMap[convertProduktNameToTaasProduktName(produktlinie.produktName)] =
        wertsachensumme.summe;
    }
  });

  return wertsachensummeMap;
}

function getWertsachensummeForProduktlinie(
  wertsachensummen: Array<Wertsachensumme>,
  produktlinie: Produktlinie,
): Wertsachensumme {
  return wertsachensummen.find(
    wertsachensumme => wertsachensumme.produktline === produktlinie.produktName,
  );
}
